import { useEffect, useState } from "react";
import "./DocumentPreview.css";

/**
 * Minimal shape both DocumentManager and UploadInbox pass in — the
 * component intentionally does not import either module's full
 * `Document` interface so it stays reusable from anywhere with these
 * four fields.
 */
export interface PreviewableDocument {
  id: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
}

/**
 * Types the server renders to HTML via GET /api/documents/:id/preview.
 * Client-side we treat them as viewable through the preview modal via
 * an iframe `srcdoc`, so the JWT stays in the fetch header. Other
 * viewable types (PDF/image/plain text) use the blob-URL path below.
 */
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function needsHtmlPreview(mimeType: string): boolean {
  const lower = mimeType.toLowerCase();
  return lower === DOCX_MIME || lower === "text/csv";
}

/**
 * Which mime types render inline in the preview modal. Rendering path:
 *  - docx, csv → HTML rendered by the server, loaded via srcdoc.
 *  - PDF, plain text, JSON, XML → blob URL in an iframe.
 *  - image/* → blob URL in an <img>.
 * Non-viewable types should fall back to a direct download at the caller
 * — the modal itself won't be opened.
 */
export function isViewable(mimeType: string): boolean {
  if (!mimeType) return false;
  if (mimeType === "application/pdf") return true;
  if (mimeType.startsWith("image/")) return true;
  if (mimeType.startsWith("text/")) return true;
  if (mimeType === "application/json" || mimeType === "application/xml")
    return true;
  if (needsHtmlPreview(mimeType)) return true;
  return false;
}

interface Props {
  doc: PreviewableDocument;
  /**
   * How to fetch the file bytes. Each caller passes its own module's
   * blob fetcher so we don't tie the component to a specific api layer.
   */
  fetchBlob: (documentId: string) => Promise<Blob>;
  /**
   * How to fetch server-rendered HTML for docx/csv previews. Optional —
   * when omitted, docx/csv fall through to `fetchBlob` and probably
   * render as garbled binary (so callers that support these types should
   * pass this). Each caller passes its own module's helper so the shared
   * component stays module-agnostic.
   */
  fetchPreviewHtml?: (documentId: string) => Promise<string>;
  onClose: () => void;
  /**
   * Optional — when provided, the modal header shows a Download button.
   * Omit to hide it (e.g., surfaces that only care about read-only
   * viewing).
   */
  onDownload?: () => void;
}

/**
 * Full-screen modal that previews a document. Two rendering paths:
 *  - Server-rendered HTML (docx, csv): loaded via `srcdoc` so the JWT
 *    stays in the fetch header rather than the iframe URL.
 *  - Blob URL (PDF, image, plain text, JSON/XML): fetched via
 *    `fetchBlob` and wrapped in an object URL. Also keeps the JWT out
 *    of the iframe src — avoids Traefik URL-length limits and
 *    X-Frame-Options issues that a token-in-URL pattern hits.
 *
 * Extracted from DocumentManager so both the docs list and the upload
 * queue can share the same viewer; see apps/shell/src/modules/
 * document-manager/CLAUDE.md for the queue-side usage.
 */
export function DocumentPreview({
  doc,
  fetchBlob,
  fetchPreviewHtml,
  onClose,
  onDownload,
}: Props) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [htmlDoc, setHtmlDoc] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);

  const useHtmlPreview = needsHtmlPreview(doc.mime_type) && !!fetchPreviewHtml;

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setPreviewError(false);
    setBlobUrl(null);
    setHtmlDoc(null);

    if (useHtmlPreview && fetchPreviewHtml) {
      fetchPreviewHtml(doc.id)
        .then((html) => {
          if (!cancelled) setHtmlDoc(html);
        })
        .catch(() => {
          if (!cancelled) setPreviewError(true);
        });
    } else {
      fetchBlob(doc.id)
        .then((blob) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        })
        .catch(() => {
          if (!cancelled) setPreviewError(true);
        });
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.id, useHtmlPreview, fetchBlob, fetchPreviewHtml]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const renderPreview = () => {
    if (previewError) {
      return <div className="doc-preview__error">Unable to load preview.</div>;
    }
    if (useHtmlPreview) {
      if (!htmlDoc) {
        return (
          <div className="doc-preview__loading">Loading preview…</div>
        );
      }
      return (
        <iframe
          className="doc-preview__iframe"
          srcDoc={htmlDoc}
          sandbox="allow-same-origin"
          title={doc.original_name}
        />
      );
    }
    if (!blobUrl) {
      return <div className="doc-preview__loading">Loading preview…</div>;
    }
    if (doc.mime_type.startsWith("image/")) {
      return (
        <img
          className="doc-preview__image"
          src={blobUrl}
          alt={doc.original_name}
        />
      );
    }
    return (
      <iframe
        className="doc-preview__iframe"
        src={blobUrl}
        title={doc.original_name}
      />
    );
  };

  return (
    <div className="doc-preview-overlay" onClick={onClose}>
      <div className="doc-preview" onClick={(e) => e.stopPropagation()}>
        <div className="doc-preview__header">
          <div className="doc-preview__title-group">
            <h3 className="doc-preview__title">{doc.original_name}</h3>
            <span className="doc-preview__meta">
              {doc.mime_type} &middot; {formatBytes(doc.size_bytes)}
            </span>
          </div>
          <div className="doc-preview__header-actions">
            {onDownload && (
              <button
                type="button"
                className="doc-preview__download"
                onClick={onDownload}
                title="Download"
                aria-label="Download"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 2v8m0 0L5 7m3 3 3-3M3 12v1.5a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="doc-preview__download-label">Download</span>
              </button>
            )}
            <button
              type="button"
              className="doc-preview__close"
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
        <div className="doc-preview__body">{renderPreview()}</div>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
